import { defineStore } from 'pinia'
import type {
  ConfirmOrderRequest,
  Order,
  Orderable,
  OrderableRow,
  OrderRowRequest,
} from '@/core/http/services/orders/types'
import { computed, ref, watch } from 'vue'
import { apiService } from '@/core/http/services/ApiService'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useSortingStore } from '@/stores/SortingStore'
import { sortToQuery } from '@/core/http/services/sorting/types'
import { useAuthStore } from '@/stores/auth'
import {
  requestFromOrder,
  type UpdateVtOrderRequest,
  type VtOrder,
  type VtOrderRow,
  type VtOrderRowRequest,
} from '@/core/http/services/vt-orders/types'
import type { OrderMode } from '@/stores/orders/types'
import type { Role } from '@/core/http/services/users/types'

const storeId = 'order'
export const useOrderStore = defineStore(storeId, () => {
  const openOrder = ref<Order | null>(null)
  const openVtOrder = ref<VtOrder | null>(null)
  const sortStore = useSortingStore(storeId)

  const mode = ref<OrderMode | null>(null)

  const showRepresentativeOrderAlert = ref(false)

  const promoGuestMode = ref(false)
  const isRepresentativeMode = computed<boolean>(() => mode.value === 'representative')
  const isRegularMode = computed<boolean>(() => mode.value === 'regular')

  sortStore.setSort('product.name')

  watch(
    () => sortStore.currentSort,
    () => getOpenOrder(true),
    { deep: true },
  )

  const now = ref(dayjs())
  setInterval(() => (now.value = dayjs()), 60000)

  const minDeliveryDate = computed<Dayjs>(() => {
    const fivePM = dayjs().hour(17).minute(0)

    let daysToAdd = 0
    if (now.value.isAfter(fivePM)) {
      daysToAdd = 1
    }
    return now.value.clone().add(daysToAdd, 'day')
  })

  const deliveryDate = computed<Dayjs>(() => {
    const order = isRepresentativeMode.value ? openVtOrder.value : openOrder.value
    if (order?.delivery_at) {
      return dayjs(order.delivery_at)
    } else {
      return minDeliveryDate.value
    }
  })

  async function setDeliveryDate(date: string) {
    const order = await getOpenOrder()

    switch (mode.value) {
      case 'regular':
        {
          const response = await apiService.order.update(order.id, {
            comments: order.comments,
            delivery_at: date,
            ...sortToQuery(sortStore.currentSort),
          })

          openOrder.value = response.data.data
        }
        break
      case 'representative':
        {
          const request: UpdateVtOrderRequest = {
            ...requestFromOrder(openVtOrder.value!),
            delivery_at: date,
            ...sortToQuery(sortStore.currentSort),
          }

          const response = await apiService.vtOrder.update(order.id, request)

          openVtOrder.value = response.data.data
        }
        break
      default:
        throw new Error(`Unknown order mode: ${mode.value}`)
    }
  }

  function getQuantity(productId: number, promoProductId: number | null): number {
    const key = getMappedProductKey(productId, promoProductId)
    return mappedProductQuantities.value[key] ?? 0
  }

  function getMappedProductKey(productId: number, promoProductId: number | null): string {
    return promoProductId ? `${productId}-${promoProductId}` : `${productId}`
  }

  const mappedProductQuantities = computed<Record<string, number>>(() => {
    const quantities = {} as Record<string, number>

    switch (mode.value) {
      case 'regular':
        for (const row of openOrder.value?.rows ?? []) {
          quantities[getMappedProductKey(row.product_id, null)] = row.quantity
        }
        break
      case 'representative':
        for (const row of openVtOrder.value?.rows ?? []) {
          const vtRow = row as VtOrderRow
          quantities[getMappedProductKey(vtRow.product_id, vtRow.promo_product_id)] = row.quantity
        }
        break
      default:
        throw new Error(`Unknown order mode: ${mode.value}`)
    }

    return quantities
  })

  function setMode(newMode: OrderMode) {
    switch (newMode) {
      case 'regular':
      case 'representative':
        openOrder.value = null
        openVtOrder.value = null
        break
      default:
        throw new Error(`Unknown mode: ${newMode}`)
    }

    mode.value = newMode
  }

  function setModeFromUserRole(role: Role) {
    if (role.is_representative_based) {
      setMode('representative')
    } else {
      setMode('regular')
    }
  }

  function enterPromoGuestMode() {
    if (mode.value === 'representative' && promoGuestMode.value) {
      return
    }
    setMode('representative')
    promoGuestMode.value = true
  }

  function exitPromoGuestMode() {
    if (promoGuestMode.value && useAuthStore().user?.role.is_customer_based) {
      setMode('regular')
      promoGuestMode.value = false
    }
  }

  async function getOpenOrder(force: boolean = false): Promise<Order | VtOrder> {
    if (!mode.value) {
      // get order mode from user
      const authStore = useAuthStore()
      const user = authStore.user
      if (!user) {
        throw new Error('User is not set')
      }
      setModeFromUserRole(user.role)
    }

    switch (mode.value) {
      case 'regular':
        if (!openOrder.value || force) {
          openOrder.value = (await apiService.order.getOpenOrder(sortStore.currentSort)).data.data
        }

        return openOrder.value
      case 'representative':
        if (!openVtOrder.value || force) {
          openVtOrder.value = (await apiService.vtOrder.getOpenOrder()).data.data
        }
        return openVtOrder.value
      default:
        throw new Error(`Order mode is undetermined: ${mode.value}`)
    }
  }

  async function addRegularRow(request: OrderRowRequest): Promise<Orderable<OrderableRow>> {
    const order = await getOpenOrder()

    if (!isRegularMode.value) {
      throw new Error(`Cannot add regular row in ${mode.value} mode`)
    }
    const response = await apiService.orderRow.store(order.id, request)
    const updatedOrder = response.data.data
    openOrder.value = updatedOrder

    return updatedOrder
  }

  async function addVtRow(request: VtOrderRowRequest) {
    const order = await getOpenOrder()

    if (!isRepresentativeMode.value) {
      throw new Error(`Cannot add VT row in ${mode.value} mode`)
    }

    const response = await apiService.vtOrderRow.store(order.id, request)
    openVtOrder.value = response.data.data
    return response.data.data
  }

  async function updateRegularRow(
    id: number,
    request: OrderRowRequest,
  ): Promise<Orderable<OrderableRow>> {
    if (mode.value !== 'regular') {
      throw new Error(`Cannot update regular row in ${mode.value} mode`)
    }
    const sortedRequest = {
      ...request,
      ...sortToQuery(sortStore.currentSort),
    }
    const order = await getOpenOrder()
    const response = await apiService.orderRow.update(order.id, id, sortedRequest)
    openOrder.value = response.data.data
    return response.data.data
  }

  async function updateVtRow(
    id: number,
    request: VtOrderRowRequest,
  ): Promise<Orderable<OrderableRow>> {
    if (mode.value !== 'representative') {
      throw new Error(`Cannot update VT row in ${mode.value} mode`)
    }
    const sortedRequest = {
      ...request,
      ...sortToQuery(sortStore.currentSort),
    }
    const order = await getOpenOrder()

    const response = await apiService.vtOrderRow.update(order.id, id, sortedRequest)
    openVtOrder.value = response.data.data
    return response.data.data
  }

  async function deleteRow(id: number): Promise<Orderable<OrderableRow>> {
    const order = await getOpenOrder()

    switch (mode.value) {
      case 'regular': {
        const response = await apiService.orderRow.destroy(order.id, id)
        openOrder.value = response.data.data
        return response.data.data
      }
      case 'representative': {
        const response = await apiService.vtOrderRow.destroy(order.id, id)
        openVtOrder.value = response.data.data
        return response.data.data
      }
      default:
        throw new Error(`Order mode is undetermined: ${mode.value}`)
    }
  }

  async function clear(): Promise<Orderable<OrderableRow>> {
    const order = await getOpenOrder()

    switch (mode.value) {
      case 'regular': {
        const response = await apiService.order.clear(order.id)
        openOrder.value = response.data.data
        return response.data.data
      }
      case 'representative': {
        const response = await apiService.vtOrder.clear(order.id)
        openVtOrder.value = response.data.data
        return response.data.data
      }
      default:
        throw new Error(`Order mode is undetermined: ${mode.value}`)
    }
  }

  async function setQuantity(
    productId: number,
    quantity: number,
    promoProductId: number | null,
  ): Promise<void> {
    const order: Orderable<OrderableRow> = await getOpenOrder()
    const row = order.rows?.find((row: OrderableRow) => {
      if (mode.value === 'representative') {
        const vtOrderRow = row as VtOrderRow
        return vtOrderRow.product_id === productId && vtOrderRow.promo_product_id === promoProductId
      } else {
        return row.product_id === productId
      }
    })

    const request: OrderRowRequest = { product_id: productId, quantity: quantity }
    if (row) {
      if (quantity === 0 || isNaN(quantity)) {
        await deleteRow(row.id)
      } else {
        switch (mode.value) {
          case 'regular':
            await updateRegularRow(row.id, request)
            break
          case 'representative':
            {
              const vtRow = row as VtOrderRow
              await updateVtRow(vtRow.id, {
                ...request,
                promo_product_id: vtRow.promo_product_id,
              })
            }
            break
          default:
            throw new Error(`Order mode is undetermined: ${mode.value}`)
        }
      }
    } else if (quantity > 0) {
      switch (mode.value) {
        case 'representative':
          await addVtRow({
            ...request,
            promo_product_id: promoProductId,
          })
          break
        case 'regular':
          await addRegularRow(request)
          break
        default:
          throw new Error(`Order mode is undetermined: ${mode.value}`)
      }
    }
  }

  async function confirm(request: ConfirmOrderRequest): Promise<void> {
    const order = await getOpenOrder()

    switch (mode.value) {
      case 'regular':
        await apiService.order.confirm(order.id, request)
        break
      case 'representative':
        await apiService.vtOrder.confirm(order.id, request)
        break
      default:
        throw new Error(`Order mode is undetermined: ${mode.value}`)
    }

    await getOpenOrder(true)
  }

  async function checkIfHasRepresentativeOrder(): Promise<void> {
    if (!mode.value) {
      throw new Error('Order mode is not set')
    }

    if (mode.value === 'regular') {
      const res = await apiService.representativeOrderRow.index()
      if (res.data.data.length > 0) {
        showRepresentativeOrderAlert.value = true
      }
    }
  }

  const itemsInShoppingCart = computed<number>(() => {
    switch (mode.value) {
      case 'regular':
        return openOrder.value?.rows?.length ?? 0
      case 'representative':
        return openVtOrder.value?.rows?.length ?? 0
      default:
        return 0
    }
  })

  // region: vt-order specific

  const showVtCustomerSelect = ref(false)

  const vtCustomerSelectedRequired = computed<boolean>(() => {
    const order = openVtOrder.value
    if (!order || mode.value !== 'representative') {
      return false
    }

    return !order.customer_id && !order.customer_name
  })

  const vtCustomerName = computed<string | null>(() => {
    const order = openVtOrder.value
    if (!order) {
      return null
    }

    return order.customer?.name ?? order.customer_name ?? 'Selecteer klant'
  })

  // endregion

  return {
    checkIfHasRepresentativeOrder,
    clear,
    confirm,
    deliveryDate,
    enterPromoGuestMode,
    exitPromoGuestMode,
    getOpenOrder,
    getQuantity,
    isRegularMode,
    isRepresentativeMode,
    itemsInShoppingCart,
    mappedProductQuantities,
    minDeliveryDate,
    mode,
    openOrder,
    openVtOrder,
    promoGuestMode,
    setDeliveryDate,
    setModeFromUserRole,
    setQuantity,
    showRepresentativeOrderAlert,
    showVtCustomerSelect,
    sortStore,
    vtCustomerName,
    vtCustomerSelectedRequired,
  }
})
